import React, { useState } from 'react';
import { pb } from '../services/pocketbase';
import { useCatalogContext } from '../contexts/CatalogContext';
import { cleanSku, getBaseSku } from '../utils/skuUtils';
import { 
  Upload, 
  Database, 
  AlertCircle, 
  Loader, 
  Link as LinkIcon, 
  FileSpreadsheet, 
  Check, 
  X, 
  ChevronRight,
  Info,
  ShieldCheck
} from 'lucide-react';

// Chaves exclusivas de gestão interna (Borrachas, Moldes e Subestoque de Cera) que NUNCA devem ser sobrescritas por planilhas
const PROTECTED_FULL_DATA_KEYS = new Set([
  'tem_molde',
  'processo_fabricacao',
  'tipo_processo',
  'config_borracha',
  'subestoque_cera'
]);

// Campos de Anúncio / SEO editáveis pelo Gerador de IA na Ficha do Produto
const AI_SEO_KEYS = new Set([
  'page_title',
  'description_small',
  'page_description',
  'page_keywords',
  'description_full'
]);

export default function CatalogImporter() {
  const { refreshCatalog, moldeSkuMap = {} } = useCatalogContext();

  const [loading, setLoading] = useState(false);
  const [isPrechecking, setIsPrechecking] = useState(false);
  const [importMode, setImportMode] = useState('file'); // 'file' ou 'url'
  const [urlInput, setUrlInput] = useState('');
  const [progress, setProgress] = useState({ total: 0, current: 0, updated: 0, created: 0, errors: 0 });
  
  // Opções de blindagem configuráveis pelo usuário antes de importar
  const [preserveAiSeo, setPreserveAiSeo] = useState(true);
  const [syncPriceWithFicha, setSyncPriceWithFicha] = useState(true);

  // Estados para sumário pré-importação
  const [summary, setSummary] = useState(null); // { total: 0, toCreate: 0, toUpdate: 0, preview: [] }
  const [rowsToImport, setRowsToImport] = useState([]); // Listagem completa de rows pré-processadas
  const [existingRecordsMap, setExistingRecordsMap] = useState(new Map());

  // --- FAZ A LEITURA DA PLANILHA E GERA O SUMÁRIO (Pré-check) ---
  const analyzeWorkbook = async (workbook) => {
    setIsPrechecking(true);
    setLoading(true);
    try {
      const XLSX = await import('xlsx'); // Carregamento sob demanda (Code Splitting)
      const wsname = workbook.SheetNames[0];
      const ws = workbook.Sheets[wsname];
      const rawData = XLSX.utils.sheet_to_json(ws);

      if (rawData.length === 0) {
        alert("A planilha importada está vazia!");
        setLoading(false);
        setIsPrechecking(false);
        return;
      }

      // 1. Busca todos os registros já existentes no catálogo em memória (evita N+1 getFirstListItem no loop)
      const existingRecords = await pb.collection('catalog').getFullList({ 
        fields: 'id,sku,name,image_url,full_data',
        batch: 1000,
        requestKey: null
      });

      const recordsBySku = new Map();
      existingRecords.forEach(r => {
        const clean = cleanSku(r.sku || r.full_data?.sku);
        if (clean) {
          recordsBySku.set(clean, r);
        }
        const rawTrim = String(r.sku || '').trim().toUpperCase();
        if (rawTrim && !recordsBySku.has(rawTrim)) {
          recordsBySku.set(rawTrim, r);
        }
      });

      let toCreate = 0;
      let toUpdate = 0;
      const dedupedRowsMap = new Map();

      rawData.forEach((row) => {
        const skuRaw = row['sku'] || row['SKU'] || row['Referência'] || row['id'];
        if (!skuRaw) return;

        const sku = cleanSku(skuRaw) || String(skuRaw).trim().toUpperCase();
        if (!sku) return;

        const name = String(row['name'] || row['Nome'] || row['page_title'] || "Sem Nome").trim();
        const existingRecord = recordsBySku.get(sku) || null;
        const exists = Boolean(existingRecord);

        dedupedRowsMap.set(sku, {
          sku,
          name,
          exists,
          existingRecord,
          row
        });
      });

      const validRows = Array.from(dedupedRowsMap.values());
      validRows.forEach(item => {
        if (item.exists) toUpdate++;
        else toCreate++;
      });

      const previewRows = validRows.slice(0, 5);

      setExistingRecordsMap(recordsBySku);
      setRowsToImport(validRows);
      setSummary({
        total: validRows.length,
        toCreate,
        toUpdate,
        preview: previewRows
      });

    } catch (err) {
      console.error("Erro no pré-processamento do arquivo:", err);
      alert("Erro ao ler dados da planilha. Verifique o arquivo.");
    } finally {
      setLoading(false);
      setIsPrechecking(false);
    }
  };

  // --- EFETUA A IMPORTAÇÃO DE FATO ---
  const executeImport = async () => {
    if (rowsToImport.length === 0) return;

    setLoading(true);
    setProgress({ total: rowsToImport.length, current: 0, updated: 0, created: 0, errors: 0 });

    try {
      // Se o usuário optou por sincronizar preços com a Ficha (produto_dados), carrega os registros que têm preco_venda_manual
      const fichaBySkuPai = new Map();
      if (syncPriceWithFicha) {
        try {
          const pdList = await pb.collection('produto_dados').getFullList({
            fields: 'id,sku_pai,preco_venda_manual,preco_venda_centavos,ficha_tecnica',
            requestKey: null
          });
          pdList.forEach(pd => {
            const pai = getBaseSku(cleanSku(pd.sku_pai));
            if (pai) fichaBySkuPai.set(pai, pd);
          });
        } catch (pdErr) {
          console.warn("[CatalogImporter] Aviso ao carregar produto_dados para sincronia de preço:", pdErr);
        }
      }

      const BATCH_SIZE = 25;
      let processedCount = 0;
      let createdCount = 0;
      let updatedCount = 0;
      let errorCount = 0;
      const syncedSkusPai = new Set();

      for (let i = 0; i < rowsToImport.length; i += BATCH_SIZE) {
        const batch = rowsToImport.slice(i, i + BATCH_SIZE);
        
        const promises = batch.map(async ({ sku, name, exists, existingRecord, row }) => {
          try {
            const skuPai = getBaseSku(sku) || sku;
            const isKnownMolde = Boolean(moldeSkuMap[skuPai] || moldeSkuMap[sku]);

            if (exists && existingRecord) {
              const existing = existingRecord;
              const existingFullData = existing.full_data || {};
              const mergedFullData = { ...existingFullData };
              
              // Detecta se a foto atual foi enviada manualmente para o PocketBase na Ficha do Produto
              const hasCustomPbCover = Boolean(
                existing.image_url && String(existing.image_url).includes('/api/files/')
              );

              Object.keys(row).forEach((key) => {
                const newVal = row[key];
                if (newVal === undefined || newVal === null || newVal === "") return;

                // 1. Protege chaves de Borracha, Molde e Subestoque de Cera
                if (PROTECTED_FULL_DATA_KEYS.has(key)) return;

                // 2. Protege Foto de Capa personalizada da Ficha do Produto
                if (hasCustomPbCover && (key === 'image_default' || key === 'image_url')) return;

                // 3. Protege JSON estruturado de tags contra sobrescrita por texto simples plano
                if (key === 'tags') {
                  const oldTags = existingFullData.tags;
                  const oldIsStructured = Array.isArray(oldTags) || (typeof oldTags === 'string' && oldTags.trim().startsWith('['));
                  const newIsStructured = Array.isArray(newVal) || (typeof newVal === 'string' && newVal.trim().startsWith('['));
                  if (oldIsStructured && !newIsStructured) {
                    return;
                  }
                }

                // 4. Protege textos de Anúncio/SEO gerados por IA caso a opção esteja marcada e já exista valor
                if (preserveAiSeo && AI_SEO_KEYS.has(key)) {
                  const existingText = existingFullData[key];
                  if (existingText && String(existingText).trim() !== "") {
                    return;
                  }
                }

                mergedFullData[key] = newVal;
              });

              // Garante que o SKU limpo e o flag de molde ativo sejam preservados
              mergedFullData.sku = sku;
              if (isKnownMolde || existingFullData.tem_molde) {
                mergedFullData.tem_molde = true;
                mergedFullData.processo_fabricacao = existingFullData.processo_fabricacao || "borracha";
              }

              // Atualiza o nome se a planilha enviou um nome válido, mantendo fallback seguro
              const incomingNameValid = name && name !== "Sem Nome";
              const finalName = incomingNameValid
                ? name
                : (existing.name && existing.name !== "Sem Nome" ? existing.name : name);

              // Atualiza imagem de capa apenas se não houver capa manual do PocketBase
              const incomingImg = row['image_default'] || row['images'] || "";
              const finalImageUrl = hasCustomPbCover
                ? existing.image_url
                : (incomingImg || existing.image_url || "");

              const updatePayload = {
                sku: sku,
                name: finalName,
                image_url: finalImageUrl,
                full_data: mergedFullData
              };

              await pb.collection('catalog').update(existing.id, updatePayload, { requestKey: null });
              updatedCount++;
            } else {
              // Cria um registro novo
              const newFullData = { ...row, sku };
              if (isKnownMolde) {
                newFullData.tem_molde = true;
                newFullData.processo_fabricacao = "borracha";
              }

              const createPayload = {
                sku: sku,
                name: name,
                image_url: row['image_default'] || row['images'] || "", 
                full_data: newFullData 
              };
              await pb.collection('catalog').create(createPayload, { requestKey: null });
              createdCount++;
            }

            // 5. Sincroniza Preço de Venda com a Ficha do Produto (produto_dados) se houver preço na planilha
            if (syncPriceWithFicha && !syncedSkusPai.has(skuPai)) {
              const rawPrice = row['price'] ?? row['Preço'] ?? row['preco'] ?? row['Valor'];
              const parsedPrice = typeof rawPrice === 'number'
                ? rawPrice
                : (typeof rawPrice === 'string' ? parseFloat(rawPrice.replace('R$', '').replace(/\s/g, '').replace(',', '.')) : NaN);

              if (!isNaN(parsedPrice) && parsedPrice > 0) {
                syncedSkusPai.add(skuPai);
                const pdRec = fichaBySkuPai.get(skuPai);
                if (pdRec && (pdRec.preco_venda_manual || pdRec.ficha_tecnica?.preco_venda_manual)) {
                  const currentFt = pdRec.ficha_tecnica && typeof pdRec.ficha_tecnica === 'object' ? pdRec.ficha_tecnica : {};
                  await pb.collection('produto_dados').update(pdRec.id, {
                    preco_venda_manual: parsedPrice,
                    preco_venda_centavos: Math.round(parsedPrice * 100),
                    ficha_tecnica: {
                      ...currentFt,
                      preco_venda_manual: parsedPrice,
                      preco_venda_centavos: Math.round(parsedPrice * 100)
                    }
                  }, { requestKey: null }).catch(() => {});
                }
              }
            }
          } catch (err) {
            console.error(`Erro ao importar o SKU ${sku}:`, err);
            errorCount++;
          }
        });

        await Promise.all(promises);
        processedCount += batch.length;
        setProgress({ 
          total: rowsToImport.length, 
          current: Math.min(processedCount, rowsToImport.length),
          created: createdCount,
          updated: updatedCount,
          errors: errorCount
        });
      }

      // Atualiza o cache local (IndexedDB) e o estado em memória do CatalogContext de uma só vez
      if (refreshCatalog) {
        await refreshCatalog();
      }

      alert(`Processo Finalizado! \nNovos Adicionados: ${createdCount} \nExistentes Atualizados: ${updatedCount} \nErros: ${errorCount}`);
      
      // Reseta estados pós-conclusão
      setSummary(null);
      setRowsToImport([]);
      setExistingRecordsMap(new Map());

    } catch (error) {
      console.error("Erro fatal na execução:", error);
      alert("Ocorreu um erro fatal durante a importação.");
    } finally {
      setLoading(false);
    }
  };

  // --- CANCELA/DESPERDIÇA A SELEÇÃO ATUAL ---
  const cancelImport = () => {
    setSummary(null);
    setRowsToImport([]);
    setUrlInput("");
  };

  // --- SELEÇÃO DE ARQUIVO LOCAL ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const XLSX = await import('xlsx');
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: 'binary' });
        await analyzeWorkbook(workbook);
      } catch (err) {
        alert("Erro ao carregar arquivo de planilha.");
        setLoading(false);
      }
      e.target.value = null; // Limpa input
    };
    reader.readAsBinaryString(file);
  };

  // --- DOWNLOAD DE LINK DIRETO ---
  const handleUrlUpload = async () => {
    if (!urlInput) return alert("Cole o link primeiro!");
    setLoading(true);

    try {
      const response = await fetch(urlInput);
      if (!response.ok) throw new Error("Falha ao baixar arquivo (Erro de Rede/CORS)");
      
      const XLSX = await import('xlsx');
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      await analyzeWorkbook(workbook);
    } catch (error) {
      console.error(error);
      setLoading(false);
      alert("ERRO AO BAIXAR: O navegador bloqueou o download por segurança (CORS) ou o link expirou.\n\nRecomendação: Baixe o arquivo no seu computador e use o 'Upload de Arquivo'.");
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-lg animate-fade-in space-y-6">
      
      {/* CABEÇALHO DO IMPORTADOR */}
      <div>
        <h3 className="font-extrabold text-slate-800 text-lg flex items-center gap-2">
          <Database size={24} className="text-purple-650"/> 
          Base de Produtos (Carga do Catálogo)
        </h3>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          Importe arquivos Excel (.xlsx) de produtos para adicionar novos SKUs ou atualizar as imagens, tags e nomes canônicos do sistema. Os dados de modelagem CAD e fichas parciais de produtos serão mantidos intactos.
        </p>
      </div>

      {/* TABS DE SELEÇÃO: FILE VS LINK */}
      {!loading && !summary && (
        <div className="flex gap-3 border-b border-slate-150 pb-4">
          <button 
            onClick={() => setImportMode('file')}
            className={`text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${importMode === 'file' ? 'bg-purple-100 text-purple-750' : 'text-slate-550 hover:bg-slate-50'}`}
          >
            <FileSpreadsheet size={16}/> Upload Arquivo
          </button>
          <button 
             onClick={() => setImportMode('url')}
             className={`text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${importMode === 'url' ? 'bg-purple-100 text-purple-750' : 'text-slate-550 hover:bg-slate-50'}`}
          >
            <LinkIcon size={16}/> Via Link Direto
          </button>
        </div>
      )}

      {/* ÁREA DE PROGRESSO DE CARREGAMENTO */}
      {loading && !isPrechecking && progress.total > 0 && (
        <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 space-y-3">
          <div className="flex items-center justify-between text-xs font-bold text-slate-600">
            <span className="flex items-center gap-2">
              <Loader className="animate-spin text-purple-650" size={15}/> 
              Gravando produtos no banco: {progress.current} de {progress.total}
            </span>
            <span>{Math.round((progress.current / progress.total) * 100)}%</span>
          </div>
          
          <div className="w-full bg-slate-200 rounded-full h-3.5 overflow-hidden">
            <div className="bg-purple-650 h-full transition-all duration-300 ease-out" style={{ width: `${(progress.current / progress.total) * 100}%` }} />
          </div>

          <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-bold mt-2">
            <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 p-1.5 rounded-lg">Novos: {progress.created}</div>
            <div className="bg-blue-50 border border-blue-100 text-blue-800 p-1.5 rounded-lg">Atualizados: {progress.updated}</div>
            <div className="bg-red-50 border border-red-100 text-red-800 p-1.5 rounded-lg">Erros: {progress.errors}</div>
          </div>
        </div>
      )}

      {/* COMPONENTES DE LEITURA (SEM PROGRESSO ATIVO) */}
      {!loading && !summary && (
        <div>
          {importMode === 'file' ? (
             <label className="block border-2 border-dashed border-slate-250 hover:border-purple-550 rounded-xl p-10 text-center hover:bg-slate-50/50 transition-all cursor-pointer group">
                <input type="file" className="hidden" accept=".xlsx, .xls" onChange={handleFileUpload} />
                <Upload size={32} className="mx-auto text-slate-350 group-hover:text-purple-550 mb-2.5 transition-colors"/>
                <span className="block text-xs font-black text-slate-650">Clique ou arraste para carregar o Excel (.xlsx, .xls)</span>
                <span className="block text-[10px] text-slate-400 mt-1">A planilha deve conter colunas de SKU / Referência e Nome do Produto</span>
             </label>
          ) : (
            <div className="flex gap-2">
              <input 
                type="text" 
                placeholder="Cole o link direto da planilha aqui (https://...)" 
                className="flex-1 border border-slate-300 rounded-xl p-2.5 text-xs outline-none focus:border-purple-500 bg-slate-50 focus:bg-white transition-all font-medium"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
              />
              <button 
                onClick={handleUrlUpload}
                className="bg-purple-650 text-white px-5 py-2.5 rounded-xl font-bold text-xs hover:bg-purple-750 transition-all shadow-md active:scale-95 whitespace-nowrap cursor-pointer"
              >
                Baixar & Analisar
              </button>
            </div>
          )}
        </div>
      )}

      {/* PAINEL DE RESUMO DA PLANILHA (PRE-CHECK) */}
      {summary && !loading && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-5 animate-fade-in">
          <div className="flex items-center gap-2 text-indigo-950 font-bold border-b border-slate-200 pb-3">
            <Info size={18} className="text-indigo-650 shrink-0" />
            <h4 className="text-xs uppercase tracking-wide">Resumo da Importação Planejada</h4>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-slate-200 p-4.5 rounded-xl text-center space-y-1 shadow-sm">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Total no Arquivo</span>
              <span className="text-2xl font-black text-slate-800">{summary.total}</span>
            </div>
            
            <div className="bg-emerald-50/50 border border-emerald-200 p-4.5 rounded-xl text-center space-y-1 shadow-sm">
              <span className="text-[10px] font-black text-emerald-600 uppercase tracking-wider block">Novos (Adições)</span>
              <span className="text-2xl font-black text-emerald-700">{summary.toCreate}</span>
            </div>

            <div className="bg-blue-50/50 border border-blue-200 p-4.5 rounded-xl text-center space-y-1 shadow-sm">
              <span className="text-[10px] font-black text-blue-650 uppercase tracking-wider block">Existentes (Atualizações)</span>
              <span className="text-2xl font-black text-blue-700">{summary.toUpdate}</span>
            </div>
          </div>

          {/* PRÉVIA DOS REGISTROS */}
          <div className="space-y-2">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
              Prévia das primeiras 5 linhas:
            </span>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100 shadow-inner">
              {summary.preview.map((item, idx) => (
                <div key={idx} className="p-3 text-xs flex justify-between items-center hover:bg-slate-50 transition-colors">
                  <div className="flex flex-col">
                    <span className="font-mono font-bold text-slate-800">{item.sku}</span>
                    <span className="text-slate-500 truncate max-w-sm" title={item.name}>{item.name}</span>
                  </div>
                  <span>
                    {item.exists ? (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded font-bold text-[9px] uppercase">Atualizar</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold text-[9px] uppercase">Novo</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* OPÇÕES DE PROTEÇÃO & BLINDAGEM DOS DADOS DA FICHA E BORRACHAS */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-xs">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
              <ShieldCheck size={16} className="text-emerald-600 shrink-0" />
              <span>Regras de Proteção Ativas (Borrachas, Ceras & Ficha do Produto)</span>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Configurações de <strong>moldes de borracha</strong>, <strong>subestoque de cera</strong>, <strong>tags estruturadas</strong> e <strong>fotos de capa enviadas na ficha</strong> são protegidas automaticamente e nunca serão apagadas pela planilha.
            </p>

            <div className="pt-2 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <label className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-slate-50 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={preserveAiSeo}
                  onChange={(e) => setPreserveAiSeo(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                />
                <div>
                  <span className="font-bold text-slate-700 block">Preservar Anúncios/SEO (IA) já preenchidos</span>
                  <span className="text-[10px] text-slate-400 block">
                    Evita sobrescrever títulos, resumos e descrições HTML já gerados na Ficha do Produto.
                  </span>
                </div>
              </label>

              <label className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-slate-50 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={syncPriceWithFicha}
                  onChange={(e) => setSyncPriceWithFicha(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                />
                <div>
                  <span className="font-bold text-slate-700 block">Sincronizar Preços com a Ficha do Produto</span>
                  <span className="text-[10px] text-slate-400 block">
                    Se a planilha contiver preço novo, atualiza também o preço manual salvo na Ficha Técnica.
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* AÇÕES DE CONFIRMAÇÃO DO RESUMO */}
          <div className="flex gap-2 justify-end pt-3 border-t border-slate-200">
            <button
              onClick={cancelImport}
              className="px-4 py-2 border border-slate-350 hover:bg-slate-100 rounded-xl text-xs font-bold text-slate-650 transition-all flex items-center gap-1 active:scale-95 cursor-pointer"
            >
              <X size={14}/> Descartar
            </button>
            <button
              onClick={executeImport}
              className="px-5 py-2 bg-purple-650 hover:bg-purple-755 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-1.5 active:scale-95 cursor-pointer"
            >
              <Check size={14}/> Confirmar Importação
            </button>
          </div>
        </div>
      )}

      {/* LOADING DE ANÁLISE PRÉVIA */}
      {loading && isPrechecking && (
        <div className="py-8 bg-slate-50 border border-slate-150 rounded-2xl text-center text-xs text-slate-500 font-bold flex flex-col items-center justify-center gap-2">
          <Loader size={20} className="animate-spin text-purple-650" />
          <span>Lendo planilha e verificando base de produtos...</span>
        </div>
      )}

    </div>
  );
}